import { useState, useEffect, useRef } from 'react';
import { LOCAL_STORAGE_KEYS, localStorage as storage } from '@/constants/localStorage';
import { useLogger } from './useLogger';

export interface LineProfile {
  userId: string | null;
  displayName: string | null;
  pictureUrl: string | null;
  email: string | null;
  language: string | null;
  statusMessage: string | null;
}

export type AuthState = 'idle' | 'loading' | 'authenticating' | 'authenticated' | 'partial' | 'failed';

export function useLineProfile(): {
  lineProfile: LineProfile;
  authState: AuthState;
  error: string | null;
  retry: () => void;
  removeLineProfile: () => void;
} {
    const logger = useLogger('useLine');
  const [lineProfile, setLineProfile] = useState<LineProfile>({
    userId: null,
    displayName: null,
    pictureUrl: null,
    email: null,
    language: null,
    statusMessage: null,
  });

  const [authState, setAuthState] = useState<AuthState>('idle');
  const [error, setError] = useState<string | null>(null);

    // Use ref to track the current timeout ID
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);


  const removeLineProfile = (): void => {
    setLineProfile({
      userId: null,
      displayName: null,
      pictureUrl: null,
      email: null,
      language: null,
      statusMessage: null,
    });
    setAuthState('idle');
    setError(null);
    storage.removeItem(LOCAL_STORAGE_KEYS.LINE_USER_ID);
    storage.removeItem(LOCAL_STORAGE_KEYS.LINE_USER_DISPLAY_NAME);
    storage.removeItem(LOCAL_STORAGE_KEYS.LINE_USER_PICTURE_URL);
    storage.removeItem(LOCAL_STORAGE_KEYS.LINE_USER_EMAIL);
    storage.removeItem(LOCAL_STORAGE_KEYS.LINE_USER_LANGUAGE);
    storage.removeItem(LOCAL_STORAGE_KEYS.LINE_USER_STATUS_MESSAGE);
  }

  const retry = (): void => {
    setError(null);
    setAuthState('loading');
    
    // Clear existing data and trigger LIFF re-initialization
    removeLineProfile();
    logger.info('Retrying Line authentication');
    // Dispatch event to trigger LIFF provider retry
    window.dispatchEvent(new Event('liffRetryRequested'));
    
    // Also check stored data as fallback
    setTimeout(checkStoredData, 1000);
  };

  const checkStoredData = (): void => {
    const userId = storage.getItem(LOCAL_STORAGE_KEYS.LINE_USER_ID);
    console.log('Checking stored LINE data:', { userId });
    
    const profile = {
      userId,
      displayName: storage.getItem(LOCAL_STORAGE_KEYS.LINE_USER_DISPLAY_NAME),
      pictureUrl: storage.getItem(LOCAL_STORAGE_KEYS.LINE_USER_PICTURE_URL),
      email: storage.getItem(LOCAL_STORAGE_KEYS.LINE_USER_EMAIL),
      language: storage.getItem(LOCAL_STORAGE_KEYS.LINE_USER_LANGUAGE),
      statusMessage: storage.getItem(LOCAL_STORAGE_KEYS.LINE_USER_STATUS_MESSAGE),
    };
    
    setLineProfile(profile);
    
    if (userId) {
      // Check if we have full profile or partial profile
      const hasDisplayName = storage.getItem(LOCAL_STORAGE_KEYS.LINE_USER_DISPLAY_NAME);
      const hasPictureUrl = storage.getItem(LOCAL_STORAGE_KEYS.LINE_USER_PICTURE_URL);
      
      if (hasDisplayName || hasPictureUrl) {
        setAuthState('authenticated'); // Full profile
      } else {
        setAuthState('partial'); // Only userId available
      }
      setError(null);
    } else if (authState === 'idle') {
      setAuthState('authenticating');
    }
  };

  // Effect for initial setup and event listeners
  useEffect(() => {
    if (typeof window === 'undefined') return;

    setAuthState('loading');
    
    // Initial check for existing data
    checkStoredData();

    // Listen for full login success
    const handleLoginSuccess = (): void => {
      console.log('Received lineLoginSuccess event');
      logger.info('Line login successful');
      checkStoredData();
    };

    // Listen for partial login success  
    const handlePartialSuccess = (): void => {
      console.log('Received linePartialSuccess event');
      logger.info('Line partial login successful');
      checkStoredData();
    };

    // Listen for profile error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleProfileError = (event: any): void => {
      console.log('Received lineProfileError event', event.detail);
      setAuthState('failed');
      logger.error('Failed to get user profile', { eventDetail: event.detail });
      setError('Failed to get user profile. Please try again.');
    };

    // Listen for general login failure
    const handleLoginFailure = (): void => {
      console.log('Received lineLoginFailure event');
      setAuthState('failed');
      logger.error('Line authentication failed');
      setError('Line authentication failed. Please try again.');
    };

    window.addEventListener('lineLoginSuccess', handleLoginSuccess);
    window.addEventListener('linePartialSuccess', handlePartialSuccess);
    window.addEventListener('lineProfileError', handleProfileError as EventListener);
    window.addEventListener('lineLoginFailure', handleLoginFailure);

    return () => {
      window.removeEventListener('lineLoginSuccess', handleLoginSuccess);
      window.removeEventListener('linePartialSuccess', handlePartialSuccess);
      window.removeEventListener('lineProfileError', handleProfileError as EventListener);
      window.removeEventListener('lineLoginFailure', handleLoginFailure);
    };
  }, []);

  // Separate effect for managing the timeout based on authState changes
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Set timeout only for loading or authenticating states
    if (authState === 'loading' || authState === 'authenticating') {
      timeoutRef.current = setTimeout(() => {
        console.log('Line authentication timeout');
        setAuthState('failed');
        setError('Line authentication timed out. Please try again.');
      }, 60000); // 60 second timeout
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [authState]); // Now authState is properly in the dependency array

  return { lineProfile, authState, error, retry, removeLineProfile };
}